import { useTranslation } from 'react-i18next';
import { Box, HStack, VStack } from 'native-base';

import { Text } from '@pimm/base';
import { Address, TemperatureUnitEnum } from '@pimm/services/lib/sms-tenants';
import { ResourceLoader } from '@app/components/shared';
import { useGetWeatherForecast } from '@app/features/weather/hooks/get-weather-forecast.hook';
import { weatherCodeToCategory } from '@app/features/weather/_helper';
import { WeatherIcon } from '@app/features/weather/components/weather-icon';
import { SunriseIcon } from '@app/features/weather/icons/sunrise';
import { SunsetIcon } from '@app/features/weather/icons/sunset';
import { DropletsIcon } from '@app/features/weather/icons/droplets';
import { WidgetBox, WidgetBoxProps } from '../widget-box';
import { range } from 'lodash';
import moment from 'moment';
import { useSiteConfig } from '@app/features/store-core';

type WidgetWeatherProps = WidgetBoxProps & {

};

export const WidgetWeather = ({ ...props }: WidgetWeatherProps) => {
  const { t } = useTranslation();
  const { siteConfig } = useSiteConfig();
  const temperatureUnit = siteConfig?.config?.temperatureUnit;
  const address = siteConfig?.address;
  const siteId = siteConfig?.id;
  const { data: weather, status } = useGetWeatherForecast(!!address && !!siteId ? { address, siteId, temperatureUnit } : undefined);

  return (
    <WidgetBox {...props}>
      <ResourceLoader h="80px" isLoading={status === 'pending'} _text={{ size: 'sm' }}>
        <VStack space={1} flex={1}>
          <HStack space={1.5}>
            <VStack space={0.5} rounded="lg" alignItems="center" justifyContent="space-around" px={1} pb={1.5} bg="gray.100" minW="100px">
              <HStack space={2} alignItems="center" px={1} w="full" h={6}>
                <Box>
                  {!!weather?.time && (
                    <Text size="sm" fontWeight={600} color="gray.700">
                      {moment(weather.time).format('h:mm A')}
                    </Text>
                  )}
                </Box>

                <HStack space={0.5} flex={1} alignItems="center" justifyContent="flex-end">
                  <DropletsIcon size={3} />
                  {!!weather?.precipitation && (
                    <Text size="sm" fontWeight={500} color="black">
                      {weather.precipitation}
                    </Text>
                  )}
                </HStack>
              </HStack>

              <VStack flex={1} alignItems="center" justifyContent="center" w="full">
                <Text size="2xl" fontWeight={700} color="black" lineHeight="xs">
                  {`${weather?.currentTemp}`}
                </Text>

                <HStack alignItems="center" overflow="hidden">
                  <WeatherIcon code={weather?.code} isDay={weather?.isDay} size="22px" />
                  <Text size="sm" fontWeight={400} color="black" numberOfLines={1}>
                    {weatherCodeToCategory(weather?.code)}
                  </Text>
                </HStack>
              </VStack>

              <HStack space={2} alignItems="center" justifyContent="center" w="full">
                <Text size="sm" fontWeight={600} color="gray.700">
                  {`L:${weather?.minTemp}`}
                </Text>
                <Text size="sm" fontWeight={600} color="gray.700">
                  {`H:${weather?.maxTemp}`}
                </Text>
              </HStack>

              <HStack space={1} rounded="md" alignItems="center" justifyContent="center" px={1} w="full" h={5} bg="white">
                <HStack space={0.5} alignItems="center">
                  <SunriseIcon size="10px" />
                  <Text size="xs" fontWeight={600} color="gray.700">
                    {weather?.sunrise}
                  </Text>
                </HStack>

                <HStack space={0.5} alignItems="center">
                  <SunsetIcon size="10px" />
                  <Text size="xs" fontWeight={600} color="gray.700">
                    {weather?.sunset}
                  </Text>
                </HStack>
              </HStack>
            </VStack>

            <VStack flex={1} justifyContent="space-around" pr={0.5}>
              {range(6).map(index => {
                const hour = weather?.hourly?.[index];
                if (!hour) return null;
                return (
                  <HStack key={`hourly.${hour.time}`} alignItems="center">
                    <Box w="54px">
                      <Text size="sm" fontWeight={500}>
                        {hour.time}
                      </Text>
                    </Box>

                    <HStack space={1} flex={1} alignItems="center">
                      <Box minW="20px">
                        <WeatherIcon code={hour.weatherCode} isDay={hour.isDay} size="20px" />
                      </Box>

                      {!!hour.temperature && (
                        <Text size="sm" fontWeight={500} color="black">
                          {hour.temperature}
                        </Text>
                      )}
                    </HStack>

                    <Box minW="24px">
                      {!!hour.precipitation && (
                        <Text size="sm" fontWeight={500} textAlign="right" color="black">
                          {`${hour.precipitation}`}
                        </Text>
                      )}
                    </Box>
                  </HStack>
                );
              })}
            </VStack>
          </HStack>
        </VStack>
      </ResourceLoader>
    </WidgetBox>
  );
};
